import { useState, useMemo, useRef, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsCalendar3, BsListUl, BsGrid, BsCheckCircle, BsBuilding, BsX, BsExclamationTriangle } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import { useAuth } from '#globalContexts/AuthProvider';
import { truncateName } from '#lib/textUtils';
import { useOrgMembersBasic } from '#orgFeature/models/org/queries';

import ActivityDrawer from '../../components/activities/ActivityDrawer/ActivityDrawer';
import CrmCalendar from '../../components/calendar/CrmCalendar/CrmCalendar';
import CrmSearchBar from '../../components/common/CrmSearchBar/CrmSearchBar';
import { USER_ACTIVITY_TYPES as ACTIVITY_TYPES } from '../../constants/activityTypes';
import { useCompleteActivity, useUpdateActivity, useDeleteActivity } from '../../models/activities/mutations';
import { useActivitiesInfinite, useActivityStats, useActivitiesList } from '../../models/activities/queries';
import { useChannelTypes } from '../../models/channels/queries';
import { CLOSED_STATUSES, isOverdue } from '../../utils/activityStatus';
import { resolveChannelIcon } from '../../utils/channelIcons';
import { isCallActivity } from '../../utils/isCallActivity';

import ActivityRow from './ActivityRow';

// Date helpers
function formatTime(d) {
	return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function getDayBucket(date) {
	const now = new Date();
	const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const diff = Math.floor((d - t) / 86400000);
	if (diff < 0) return { key: 'overdue', label: 'Überfällig', order: -1 };
	if (diff === 0) return { key: 'today', label: 'Heute', order: 0 };
	if (diff === 1) return { key: 'tomorrow', label: 'Morgen', order: 1 };
	if (diff < 7) return { key: 'week', label: 'Diese Woche', order: 2 };
	return { key: 'later', label: 'Später', order: 3 };
}

function formatDayLabel(d) {
	const now = new Date();
	const diff = Math.floor(
		(new Date(d.getFullYear(), d.getMonth(), d.getDate()) -
			new Date(now.getFullYear(), now.getMonth(), now.getDate())) /
			86400000,
	);
	if (diff === -1) return 'Gestern';
	if (diff === 0) return 'Heute';
	if (diff === 1) return 'Morgen';
	if (diff > 1 && diff < 7) return d.toLocaleDateString('de-DE', { weekday: 'long' });
	return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'short' });
}

// Main page
const CrmActivitiesPage = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const { data: membersData } = useOrgMembersBasic();
	const members = membersData?.members ?? [];
	const { data: channelTypes = [] } = useChannelTypes(true);
	const activeChannels = channelTypes.filter((c) => c.orgEnabled !== false);

	// View & filter state
	const [view, setView] = useState('list');
	const [showDone, setShowDone] = useState(false);
	const [showAutomated, setShowAutomated] = useState(false);
	const [typeFilters, setTypeFilters] = useState([]);
	const [channelFilters, setChannelFilters] = useState([]);
	const [assigneeFilters, setAssigneeFilters] = useState([]);
	const [smartList, setSmartList] = useState('all');
	const [openId, setOpenId] = useState(null);
	const [completingId, setCompletingId] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [callReachedActivityId, setCallReachedActivityId] = useState(null);

	const scrollContainerRef = useRef(null);
	const sentinelRef = useRef(null);

	const toggleType = (t) => {
		setTypeFilters((f) => (f.includes(t) ? f.filter((x) => x !== t) : [...f, t]));
		if (t === 'communication') setChannelFilters([]);
	};
	const toggleChannel = (id) => setChannelFilters((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
	const toggleAssignee = (userId) =>
		setAssigneeFilters((f) => (f.includes(userId) ? f.filter((x) => x !== userId) : [...f, userId]));
	const clearFilters = () => {
		setTypeFilters([]);
		setChannelFilters([]);
		setAssigneeFilters([]);
		setSmartList('all');
		setShowDone(false);
	};

	// Resolve assignee IDs for server filter
	const resolvedUserIds = useMemo(() => {
		return assigneeFilters.map((id) => (id === 'me' ? user?.id : id)).filter(Boolean);
	}, [assigneeFilters, user?.id]);

	// Build server-side filters from UI state
	const serverFilters = useMemo(() => {
		const now = new Date();
		const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
		const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();

		const f = {
			orderBy: 'dueAt',
			orderDir: 'desc',
			excludeActivityTypes: 'stage_change,system',
		};

		if (showDone) {
			f.status = 'completed';
			f.orderBy = 'completedAt';
		} else {
			// A cancelled activity is closed — the user pulled it. A failed one stays visible so the
			// failure gets noticed; it just no longer renders as overdue.
			f.statusNotIn = CLOSED_STATUSES.join(',');
			if (smartList === 'overdue') {
				f.dueBefore = todayStart;
				f.excludeActivityTypes = 'stage_change,system,meeting';
			} else if (smartList === 'today') {
				f.dueAfter = todayStart;
				f.dueBefore = todayEnd;
			}
		}

		if (!showAutomated) {
			f.excludeAutomated = true;
		}

		if (typeFilters.length > 0) {
			f.activityTypes = typeFilters.join(',');
		}
		if (channelFilters.length > 0) {
			f.channelTypeIds = channelFilters.join(',');
		}
		if (resolvedUserIds.length > 0) {
			f.userIds = resolvedUserIds.join(',');
		}

		return f;
	}, [showDone, showAutomated, smartList, typeFilters, channelFilters, resolvedUserIds]);

	// Stats filters — assignee + automated toggle so counts match the list
	const statsFilters = useMemo(() => {
		const f = {};
		if (resolvedUserIds.length > 0) f.userIds = resolvedUserIds.join(',');
		if (!showAutomated) f.excludeAutomated = true;
		return f;
	}, [resolvedUserIds, showAutomated]);

	// Data fetching
	const isListView = view === 'list';
	const {
		data: pagesData,
		isLoading: infiniteLoading,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useActivitiesInfinite(serverFilters);

	const { data: bulkData, isLoading: bulkLoading } = useActivitiesList(
		{ ...serverFilters, pageSize: 200 },
		{ enabled: !isListView },
	);

	const { data: stats = { total: 0, overdue: 0, today: 0, done: 0 } } = useActivityStats(statsFilters);

	const allActivities = useMemo(
		() => (isListView ? (pagesData?.pages?.flatMap((p) => p.activities) ?? []) : (bulkData?.activities ?? [])),
		[isListView, pagesData?.pages, bulkData?.activities],
	);
	const isLoading = isListView ? infiniteLoading : bulkLoading;
	const totalResultCount = isListView
		? (pagesData?.pages?.[0]?.total ?? 0)
		: (bulkData?.total ?? allActivities.length);

	const completeMutation = useCompleteActivity();
	const updateMutation = useUpdateActivity();
	const deleteMutation = useDeleteActivity();

	// IntersectionObserver for infinite scroll (list view only)
	useEffect(() => {
		if (!isListView || !hasNextPage || isFetchingNextPage) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) fetchNextPage();
			},
			{ root: scrollContainerRef.current, rootMargin: '200px' },
		);
		if (sentinelRef.current) observer.observe(sentinelRef.current);
		return () => observer.disconnect();
	}, [isListView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	// Group by date bucket for list view
	const grouped = useMemo(() => {
		const buckets = {};
		allActivities.forEach((a) => {
			const date = new Date(a.dueAt ?? a.createdAt);
			const b = a.dueAt ? getDayBucket(date) : { key: 'later', label: 'Später', order: 3 };
			if (!buckets[b.key]) buckets[b.key] = { ...b, items: {} };
			const dayKey = date.toDateString();
			if (!buckets[b.key].items[dayKey]) buckets[b.key].items[dayKey] = { date, list: [] };
			buckets[b.key].items[dayKey].list.push(a);
		});
		return Object.values(buckets).sort((a, b) => a.order - b.order);
	}, [allActivities]);

	const handleComplete = async (e, activity, reached) => {
		e.stopPropagation();
		if (completingId) return;
		// For calls that are not yet completed, show the reached popover first
		if (reached === null && activity.status !== 'completed' && isCallActivity(activity, channelTypes)) {
			setCallReachedActivityId(activity.id);
			return;
		}
		setCompletingId(activity.id);
		try {
			if (activity.status === 'completed') {
				await updateMutation.mutateAsync({ id: activity.id, payload: { status: 'planned' } });
			} else {
				const payload = reached !== null ? { channelPayload: { reached } } : {};
				await completeMutation.mutateAsync({ id: activity.id, payload });
			}
		} finally {
			setCompletingId(null);
		}
	};

	const handleCallReachedSelect = (reached) => {
		const activity = allActivities.find((a) => a.id === callReachedActivityId);
		if (activity) {
			handleComplete({ stopPropagation: () => {} }, activity, reached);
		}
		setCallReachedActivityId(null);
	};

	const handleCancelAutomated = async (activity) => {
		try {
			await updateMutation.mutateAsync({ id: activity.id, payload: { status: 'cancelled' } });
		} catch {
			/* handled by mutation */
		}
	};

	const handleDelete = (id) => {
		const activity = allActivities.find((a) => a.id === id);
		setDeleteTarget(activity ?? { id });
	};

	const confirmDelete = async () => {
		if (!deleteTarget) return;
		await deleteMutation.mutateAsync(deleteTarget.id);
		if (openId === deleteTarget.id) setOpenId(null);
		setDeleteTarget(null);
	};

	const handleReassign = async (id, userId) => {
		await updateMutation.mutateAsync({ id, payload: { userId } });
	};

	const openActivity = allActivities.find((a) => a.id === openId);

	const activeFilterCount =
		(typeFilters.length > 0 ? 1 : 0) + (channelFilters.length > 0 ? 1 : 0) + (assigneeFilters.length > 0 ? 1 : 0);

	return (
		<Page
			scrollMode="content"
			header={
				<PageHeader breadcrumbs={[{ label: 'CRM', to: '/crm' }, 'Aktivitäten']}>
					<div className="w-64">
						<CrmSearchBar />
					</div>
				</PageHeader>
			}
		>
			<div className="flex flex-1 min-h-0">
				{/* Left sidebar */}
				<aside className="w-60 flex-shrink-0 border-r border-bgBase-500 bg-white p-3.5 overflow-y-auto flex flex-col gap-4">
					{/* Smart lists */}
					<div className="flex flex-col gap-1">
						<span className="text-[10px] font-bold uppercase tracking-wider text-bgBase-800 px-1 mb-1">
							Smart-Listen
						</span>
						<SmartListBtn
							icon={BsListUl}
							label="Alle"
							count={stats.total}
							active={smartList === 'all' && !showDone}
							onClick={() => {
								setSmartList('all');
								setShowDone(false);
							}}
						/>
						<SmartListBtn
							icon={BsExclamationTriangle}
							label="Überfällig"
							count={stats.overdue}
							active={smartList === 'overdue' && !showDone}
							onClick={() => {
								setSmartList('overdue');
								setShowDone(false);
							}}
							danger
						/>
						<SmartListBtn
							icon={BsCalendar3}
							label="Heute"
							count={stats.today}
							active={smartList === 'today' && !showDone}
							onClick={() => {
								setSmartList('today');
								setShowDone(false);
							}}
						/>
						<SmartListBtn
							icon={BsCheckCircle}
							label="Erledigt"
							count={stats.done}
							active={showDone}
							onClick={() => {
								setShowDone(true);
								setSmartList('all');
							}}
						/>
					</div>

					{/* Activity type filter */}
					<div className="flex flex-col gap-1.5">
						<span className="text-[10px] font-bold uppercase tracking-wider text-bgBase-800 px-1">
							Aktivitätstyp
						</span>
						<div className="grid grid-cols-2 gap-1">
							{ACTIVITY_TYPES.map((t) => {
								const active = typeFilters.includes(t.id);
								const Icon = t.icon;
								return (
									<button
										key={t.id}
										type="button"
										onClick={() => toggleType(t.id)}
										className={`inline-flex items-center gap-1 px-2 py-1.5 text-[11px] font-medium border rounded-control transition-colors ${
											active
												? 'font-semibold'
												: 'border-bgBase-500 bg-white text-bgBase-800 hover:bg-bgBase-300'
										}`}
										style={
											active
												? { background: t.color + '1a', borderColor: t.color, color: t.color }
												: {}
										}
									>
										<Icon className="w-3 h-3" /> {t.label}
									</button>
								);
							})}
						</div>

						{/* Channel sub-filters (shown when communication is active) */}
						{typeFilters.includes('communication') && activeChannels.length > 0 && (
							<div className="flex flex-wrap gap-1 pl-1 pt-0.5">
								{activeChannels.map((ch) => {
									const active = channelFilters.includes(ch.id);
									const ChIcon = resolveChannelIcon(ch);
									return (
										<button
											key={ch.id}
											type="button"
											onClick={() => toggleChannel(ch.id)}
											className={`inline-flex items-center gap-1 px-1.5 py-1 text-[10px] font-medium border rounded-control transition-colors ${
												active
													? 'border-primary-300 bg-primary-100 text-primary-700 font-semibold'
													: 'border-bgBase-500 bg-white text-bgBase-800 hover:bg-bgBase-300 hover:text-text'
											}`}
										>
											<ChIcon className="w-2.5 h-2.5" /> {ch.displayName}
										</button>
									);
								})}
							</div>
						)}
					</div>

					{/* Owner filter — avatar circles */}
					<div className="flex flex-col gap-1.5">
						<span className="text-[10px] font-bold uppercase tracking-wider text-bgBase-800 px-1">
							Owner
						</span>
						<div className="flex flex-wrap gap-1.5">
							{members.map((m) => {
								const active = assigneeFilters.includes(m.userId);
								const initials = (m.fullName ?? m.email ?? '?')
									.split(' ')
									.map((w) => w[0])
									.slice(0, 2)
									.join('')
									.toUpperCase();
								return (
									<button
										key={m.userId}
										type="button"
										onClick={() => toggleAssignee(m.userId)}
										title={m.fullName ?? m.email}
										className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-semibold transition-colors ${
											active
												? 'bg-primary-600 text-white ring-2 ring-primary-300 ring-offset-1'
												: 'bg-bgBase-400 text-bgBase-800 hover:bg-bgBase-500'
										}`}
									>
										{initials}
									</button>
								);
							})}
						</div>
					</div>

					<div className="px-1">
						<Checkbox
							id="activities-show-automated"
							checked={showAutomated}
							onChange={() => setShowAutomated((s) => !s)}
							label="Automatisierte anzeigen"
						/>
					</div>
				</aside>

				{/* Main content */}
				<div ref={scrollContainerRef} className="flex-1 min-w-0 overflow-y-auto p-4 lg:p-6">
					{/* Header with stats + view switch */}
					<div className="flex items-start justify-between gap-4 mb-4">
						<div>
							<h1 className="text-lg font-bold text-text tracking-tight">Aktivitäten</h1>
							<div className="flex items-center gap-2 text-xs text-bgBase-800 mt-1">
								{stats.overdue > 0 && (
									<>
										<span className="font-semibold text-warning-500">{stats.overdue}</span>{' '}
										überfällig
										<span className="text-bgBase-800">·</span>
									</>
								)}
								<span className="font-semibold text-text">{stats.today}</span> heute
								<span className="text-bgBase-800">·</span>
								<span className="text-bgBase-800">{stats.total} offen</span>
							</div>
						</div>
						<div className="flex items-center gap-1 bg-bgBase-400 rounded-card p-0.5">
							{[
								{ key: 'list', icon: BsListUl, label: 'Liste' },
								{ key: 'calendar', icon: BsCalendar3, label: 'Woche' },
								{ key: 'board', icon: BsGrid, label: 'Board' },
							].map((v) => (
								<button
									key={v.key}
									type="button"
									onClick={() => setView(v.key)}
									className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium rounded-control transition-colors ${
										view === v.key ? 'bg-white text-primary-600' : 'text-bgBase-800 hover:text-text'
									}`}
								>
									<v.icon className="w-3.5 h-3.5" /> {v.label}
								</button>
							))}
						</div>
					</div>

					{/* Active filter chips */}
					{activeFilterCount > 0 && (
						<div className="flex items-center gap-1.5 flex-wrap pb-3 mb-3 border-b border-dashed border-bgBase-500">
							{typeFilters.map((t) => {
								const meta = ACTIVITY_TYPES.find((x) => x.id === t);
								return (
									<span
										key={t}
										className="inline-flex items-center gap-1 text-[11px] font-medium bg-primary-100 text-primary-700 border border-primary-100 rounded-full pl-2.5 pr-1 py-0.5"
									>
										{meta?.label}
										<button
											type="button"
											onClick={() => toggleType(t)}
											className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-primary-100"
										>
											<BsX className="w-3 h-3" />
										</button>
									</span>
								);
							})}
							{channelFilters.map((id) => {
								const ch = activeChannels.find((c) => c.id === id);
								if (!ch) return null;
								return (
									<span
										key={id}
										className="inline-flex items-center gap-1 text-[11px] font-medium bg-primary-100 text-primary-700 border border-primary-200 rounded-full pl-2.5 pr-1 py-0.5"
									>
										{ch.displayName}
										<button
											type="button"
											onClick={() => toggleChannel(id)}
											className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-primary-200"
										>
											<BsX className="w-3 h-3" />
										</button>
									</span>
								);
							})}
							{assigneeFilters.map((uid) => {
								const m = members.find((x) => x.userId === uid);
								return (
									<span
										key={uid}
										className="inline-flex items-center gap-1 text-[11px] font-medium bg-primary-100 text-primary-700 border border-primary-100 rounded-full pl-2.5 pr-1 py-0.5"
									>
										{m?.fullName ?? '?'}
										<button
											type="button"
											onClick={() => toggleAssignee(uid)}
											className="w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-primary-100"
										>
											<BsX className="w-3 h-3" />
										</button>
									</span>
								);
							})}
							<button
								type="button"
								onClick={clearFilters}
								className="text-[11px] text-bgBase-800 hover:text-text ml-1"
							>
								Alle zurücksetzen
							</button>
							<span className="text-[11px] text-bgBase-800 ml-auto">{totalResultCount} Ergebnisse</span>
						</div>
					)}

					{/* Loading */}
					{isLoading && <p className="text-sm text-bgBase-800 py-8 text-center">Wird geladen …</p>}

					{/* Empty state */}
					{!isLoading && allActivities.length === 0 && (
						<div className="flex flex-col items-center gap-3 py-16 text-center">
							<BsCheckCircle className="w-8 h-8 text-bgBase-800" />
							<h3 className="text-sm font-semibold text-text">Alles erledigt</h3>
							<p className="text-xs text-bgBase-800">Keine Aktivitäten in dieser Ansicht.</p>
						</div>
					)}

					{/* List view */}
					{!isLoading && view === 'list' && allActivities.length > 0 && (
						<div className="flex flex-col gap-5">
							{grouped.map((bucket) => (
								<div key={bucket.key}>
									<div className="flex items-center gap-2 mb-2">
										<span
											className={`text-xs font-bold uppercase tracking-wider ${
												bucket.key === 'overdue'
													? 'text-warning-500'
													: bucket.key === 'today'
														? 'text-primary-700'
														: 'text-bgBase-800'
											}`}
										>
											{bucket.label}
										</span>
										<span className="text-[10px] text-bgBase-800 bg-bgBase-400 rounded-full px-1.5 py-0.5 font-semibold">
											{Object.values(bucket.items).reduce((s, d) => s + d.list.length, 0)}
										</span>
										<div className="flex-1 h-px bg-bgBase-500" />
									</div>
									{Object.values(bucket.items).map((dayGroup) => (
										<div key={dayGroup.date.toISOString()}>
											{bucket.key !== 'today' &&
												bucket.key !== 'tomorrow' &&
												bucket.key !== 'overdue' && (
													<div className="flex items-center gap-1.5 text-[11px] text-bgBase-800 mb-1 ml-1">
														<BsCalendar3 className="w-3 h-3" />
														{formatDayLabel(dayGroup.date)} ·{' '}
														{dayGroup.date.toLocaleDateString('de-DE', {
															day: '2-digit',
															month: 'short',
														})}
													</div>
												)}
											{dayGroup.list.map((a) => (
												<ActivityRow
													key={a.id}
													activity={a}
													active={openId === a.id}
													members={members}
													onClick={() => setOpenId(openId === a.id ? null : a.id)}
													onComplete={handleComplete}
													onCancelAutomated={handleCancelAutomated}
													onDelete={handleDelete}
													onReassign={handleReassign}
													completingId={completingId}
													callReachedActivityId={callReachedActivityId}
													onCallReachedSelect={handleCallReachedSelect}
													onCallReachedCancel={() => setCallReachedActivityId(null)}
												/>
											))}
										</div>
									))}
								</div>
							))}

							{/* Sentinel for infinite scroll */}
							{hasNextPage && <div ref={sentinelRef} className="h-1" />}
							{isFetchingNextPage && (
								<div className="flex items-center justify-center py-3">
									<div className="w-4 h-4 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
								</div>
							)}
						</div>
					)}

					{/* Calendar week view */}
					{!isLoading && view === 'calendar' && (
						<Card className="p-3 overflow-hidden">
							<CrmCalendar
								activities={allActivities}
								initialView="timeGridWeek"
								onEventClick={(activity) => setOpenId(activity.id)}
								headerToolbar={false}
								height={600}
							/>
						</Card>
					)}

					{/* Board view */}
					{!isLoading && view === 'board' && <BoardView activities={allActivities} setOpenId={setOpenId} />}
				</div>

				{/* Detail drawer */}
				{openActivity && (
					<ActivityDrawer
						activity={openActivity}
						members={members}
						onClose={() => setOpenId(null)}
						onComplete={handleComplete}
						onCancelAutomated={handleCancelAutomated}
						onDelete={handleDelete}
						onReassign={handleReassign}
						onUpdate={(id, patch) => updateMutation.mutateAsync({ id, payload: patch })}
						navigate={navigate}
					/>
				)}
			</div>

			<ConfirmModal
				isOpen={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={confirmDelete}
				title={`Aktivität "${deleteTarget?.title ?? ''}" löschen`}
				message="Möchten Sie diese Aktivität wirklich löschen?"
				confirmText="Löschen"
				isLoading={deleteMutation.isPending}
			/>
		</Page>
	);
};

// Smart List Button
const SmartListBtn = ({ icon: Icon, label, count, active, onClick, danger }) => (
	<button
		type="button"
		onClick={onClick}
		className={`flex items-center gap-2 w-full px-2.5 py-[7px] rounded-control text-[12.5px] font-medium text-left transition-colors ${
			active
				? 'bg-primary-100 text-primary-700 font-semibold'
				: 'text-bgBase-800 hover:bg-bgBase-300 hover:text-text'
		}`}
	>
		<Icon className={`w-3.5 h-3.5 ${danger && !active ? 'text-warning-500' : ''}`} />
		{label}
		{count > 0 && (
			<span
				className={`ml-auto text-[11px] font-semibold rounded-full px-1.5 py-0.5 min-w-[22px] text-center ${
					active
						? 'bg-primary-200 text-primary-700'
						: danger
							? 'bg-warning-200 text-warning-700'
							: 'bg-bgBase-400 text-bgBase-800'
				}`}
			>
				{count}
			</span>
		)}
	</button>
);
SmartListBtn.propTypes = {
	icon: PropTypes.elementType,
	label: PropTypes.string,
	count: PropTypes.number,
	active: PropTypes.bool,
	onClick: PropTypes.func,
	danger: PropTypes.bool,
};

// Board View
const BoardView = ({ activities, setOpenId }) => (
	<div className="flex gap-3 overflow-x-auto pb-2">
		{ACTIVITY_TYPES.map((type) => {
			const items = activities.filter((a) => a.activityType === type.id);
			const Icon = type.icon;
			return (
				<div
					key={type.id}
					className="w-64 flex-shrink-0 bg-bgBase-300 border border-bgBase-500 rounded-card overflow-hidden"
				>
					<div
						className="px-3 py-2.5 border-b-2 flex items-center justify-between"
						style={{ borderBottomColor: type.color }}
					>
						<div className="flex items-center gap-2 text-[12px] font-semibold text-text">
							<span
								className="w-6 h-6 rounded-control flex items-center justify-center"
								style={{ background: type.color + '1a', color: type.color }}
							>
								<Icon className="w-3 h-3" />
							</span>
							{type.label}
						</div>
						<span className="text-[10px] font-semibold text-bgBase-800 bg-bgBase-400 rounded-full px-1.5 py-0.5">
							{items.length}
						</span>
					</div>
					<div className="p-2 flex flex-col gap-1.5 max-h-[calc(100vh-260px)] overflow-y-auto">
						{items.map((a) => {
							const dueDate = a.dueAt ? new Date(a.dueAt) : null;
							const overdue = isOverdue(a);
							return (
								<div
									key={a.id}
									className={`bg-white border border-bgBase-500 rounded-card p-2.5 cursor-pointer hover:border-primary-200 transition-colors ${
										a.status === 'completed' ? 'opacity-60' : ''
									} ${overdue ? 'border-l-2 border-l-warning-500' : ''}`}
									onClick={() => setOpenId(a.id)}
								>
									<div
										className={`text-[12px] font-semibold mb-1 truncate ${a.status === 'completed' ? 'line-through text-bgBase-800' : 'text-text'}`}
										title={a.title}
									>
										{truncateName(a.title)}
									</div>
									{a.companyName && (
										<div
											className="text-[10px] text-bgBase-800 flex items-center gap-1 mb-1.5"
											title={a.companyName}
										>
											<BsBuilding className="w-2.5 h-2.5" /> {truncateName(a.companyName)}
										</div>
									)}
									<div className="flex items-center justify-between">
										<span
											className={`text-[10px] ${overdue ? 'text-warning-500 font-semibold' : 'text-bgBase-800'}`}
										>
											{dueDate ? `${formatDayLabel(dueDate)}, ${formatTime(dueDate)}` : '–'}
										</span>
									</div>
								</div>
							);
						})}
						{items.length === 0 && (
							<div className="text-[11px] text-bgBase-800 text-center py-4">Keine</div>
						)}
					</div>
				</div>
			);
		})}
	</div>
);
BoardView.propTypes = { activities: PropTypes.array, setOpenId: PropTypes.func };

export default CrmActivitiesPage;
